import React from 'react';
import { Link } from '../i18n';
import { Email } from './pecas';

/**
 * Termos de Serviço, em pt, en e es. Vale o inglês; pt e es são tradução de
 * conveniência, e a MolduraJuridica diz isso a quem lê.
 *
 * Números de plano ficam de fora de propósito: quem os publica é a tela de
 * planos, que os deriva do `planConfigs`. E um texto jurídico não muda sem a
 * data mudar, o que aconteceria se ele lesse os limites ao vivo.
 *
 * As seções ficam num array só, com o mesmo `id` nos três idiomas; as remissões
 * entre seções são links para esses `id`.
 */

export const TERMOS = {
  atualizadoEm: '2026-10-23',
  titulo: { pt: 'Termos de Serviço', en: 'Terms of Service', es: 'Términos del Servicio' },
  lead: {
    pt: 'O que o serviço faz, o que você pode rastrear, como cobramos e cancelamos, e o que a IA é e não é.',
    en: 'What the service does, what you may crawl, how we charge and cancel, and what the AI is and is not.',
    es: 'Qué hace el servicio, qué puedes rastrear, cómo cobramos y cancelamos, y qué es y qué no es la IA.'
  },
  atualizado: { pt: 'Atualizados em', en: 'Last updated', es: 'Actualizados el' },
  provisoria: {
    pt: 'Versão provisória: descreve como o Silent Puma funciona hoje, mas ainda não passou por advogado e não é um texto jurídico final. Se algo aqui pesa numa decisão sua, escreva antes para',
    en: 'Provisional version: these terms describe how Silent Puma works today, but they have not been reviewed by a lawyer and are not a final legal text. If something here matters to a decision you are making, write to us first at',
    es: 'Versión provisional: describe cómo funciona Silent Puma hoy, pero aún no la revisó un abogado y no es un texto legal definitivo. Si algo aquí pesa en una decisión tuya, escribe antes a'
  },
  resumo: [
    {
      ancora: 'rastrear',
      texto: {
        pt: 'Rastreie só sites seus ou que você tem autorização para analisar.',
        en: 'Only crawl sites you own or are authorized to analyze.',
        es: 'Rastrea solo sitios tuyos o que tienes autorización para analizar.'
      }
    },
    {
      ancora: 'pagamento',
      texto: {
        pt: 'O Basic é grátis e não pede cartão. Os pagos renovam até você cancelar.',
        en: 'Basic is free and needs no card. Paid plans renew until you cancel.',
        es: 'Basic es gratis y no pide tarjeta. Los planes de pago se renuevan hasta que canceles.'
      }
    },
    {
      ancora: 'pagamento',
      texto: {
        pt: 'Cancelou: você usa até o fim do período pago e volta ao Basic.',
        en: 'If you cancel, you keep access until the paid period ends, then move to Basic.',
        es: 'Si cancelas, sigues con acceso hasta el fin del período pagado y vuelves a Basic.'
      }
    },
    {
      ancora: 'ia',
      texto: {
        pt: 'A IA sugere. Quem decide o que vai para o ar é você.',
        en: 'The AI suggests. You decide what goes live.',
        es: 'La IA sugiere. Tú decides qué se publica.'
      }
    }
  ],
  secoes: [
    {
      id: 'aceite',
      titulo: { pt: 'Aceite', en: 'Acceptance', es: 'Aceptación' },
      corpo: {
        pt: (
          <p>
            Ao criar uma conta ou usar o Silent Puma, você concorda com estes termos. Se usa o serviço em nome de uma
            empresa, você confirma que pode aceitá-los por ela.
          </p>
        ),
        en: (
          <p>
            By creating an account or using Silent Puma, you agree to these terms. If you are using it on behalf of a
            company, you confirm you may accept them for that company.
          </p>
        ),
        es: (
          <p>
            Al crear una cuenta o usar Silent Puma, aceptas estos términos. Si usas el servicio en nombre de una
            empresa, confirmas que puedes aceptarlos por ella.
          </p>
        )
      }
    },
    {
      id: 'servico',
      titulo: { pt: 'O que o serviço faz', en: 'What the service does', es: 'Qué hace el servicio' },
      corpo: {
        pt: (
          <>
            <p>O Silent Puma:</p>
            <ul className="nh-lista">
              <li>Rastreia os sites que você cadastra e coleta dados técnicos de SEO de cada página</li>
              <li>Lê metadados públicos dos canais do YouTube que você cadastra</li>
              <li>Mostra esses dados em painéis que você pode filtrar</li>
              <li>Exporta os resultados em CSV</li>
              <li>Gera recomendações de IA a partir de uma execução, quando você pede</li>
              <li>
                Conecta-se, se você quiser, ao Google Analytics, ao Search Console e ao YouTube Analytics, só para
                leitura
              </li>
            </ul>
          </>
        ),
        en: (
          <>
            <p>Silent Puma:</p>
            <ul className="nh-lista">
              <li>Crawls websites you register and collects technical SEO data from each page</li>
              <li>Reads public metadata from YouTube channels you register</li>
              <li>Shows that data in dashboards you can filter</li>
              <li>Exports results as CSV</li>
              <li>Generates AI recommendations from a run, when you ask for one</li>
              <li>Connects, optionally, to Google Analytics, Search Console and YouTube Analytics in read-only mode</li>
            </ul>
          </>
        ),
        es: (
          <>
            <p>Silent Puma:</p>
            <ul className="nh-lista">
              <li>Rastrea los sitios que registras y recopila datos técnicos de SEO de cada página</li>
              <li>Lee metadatos públicos de los canales de YouTube que registras</li>
              <li>Muestra esos datos en paneles que puedes filtrar</li>
              <li>Exporta los resultados en CSV</li>
              <li>Genera recomendaciones de IA a partir de una ejecución, cuando las pides</li>
              <li>Se conecta, si quieres, a Google Analytics, Search Console y YouTube Analytics, solo para lectura</li>
            </ul>
          </>
        )
      }
    },
    {
      id: 'conta',
      titulo: { pt: 'Sua conta', en: 'Your account', es: 'Tu cuenta' },
      corpo: {
        pt: (
          <>
            <p>Você responde por:</p>
            <ul className="nh-lista">
              <li>Guardar suas credenciais só para você: as contas são pessoais</li>
              <li>A exatidão das informações que nos dá</li>
              <li>Tudo o que for feito pela sua conta</li>
            </ul>
            <p>
              Avise em <Email /> se achar que outra pessoa tem acesso a ela.
            </p>
          </>
        ),
        en: (
          <>
            <p>You are responsible for:</p>
            <ul className="nh-lista">
              <li>Keeping your credentials to yourself: accounts are personal</li>
              <li>The accuracy of the information you give us</li>
              <li>Everything done through your account</li>
            </ul>
            <p>
              Tell us at <Email /> if you believe someone else has access to it.
            </p>
          </>
        ),
        es: (
          <>
            <p>Eres responsable de:</p>
            <ul className="nh-lista">
              <li>Guardar tus credenciales solo para ti: las cuentas son personales</li>
              <li>La exactitud de la información que nos das</li>
              <li>Todo lo que se haga con tu cuenta</li>
            </ul>
            <p>
              Avísanos en <Email /> si crees que otra persona tiene acceso a ella.
            </p>
          </>
        )
      }
    },
    {
      id: 'rastrear',
      titulo: { pt: 'O que você pode rastrear', en: 'What you may crawl', es: 'Qué puedes rastrear' },
      corpo: {
        pt: (
          <>
            <p>
              Esta é a parte que mais importa. Ao cadastrar um site, você nos diz que é dono dele ou que tem autorização
              de quem é. A responsabilidade por essa autorização continua sendo sua: nós não a verificamos.
            </p>
            <p>Você concorda em não apontar o Silent Puma para:</p>
            <ul className="nh-lista">
              <li>Sites que não são seus e que você não tem permissão para analisar</li>
              <li>Áreas atrás de login, ou conteúdo que não foi feito para ser público</li>
              <li>Qualquer site de um jeito que atrapalhe o funcionamento normal dele</li>
            </ul>
            <p>
              Você controla o alcance e o limite de páginas ao configurar cada execução. Ajustá-los com
              responsabilidade para o site de destino é decisão sua, e responsabilidade sua.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              This is the part that matters most. When you register a site, you are telling us you own it or are
              authorized by whoever does. You remain responsible for that authorization: we do not verify it.
            </p>
            <p>You agree not to point Silent Puma at:</p>
            <ul className="nh-lista">
              <li>Sites you neither own nor have permission to analyze</li>
              <li>Areas behind a login, or content not meant to be public</li>
              <li>Any site in a way that disrupts its normal operation</li>
            </ul>
            <p>
              You control crawl depth and page limits when configuring a run. Setting them responsibly for the target
              site is your call, and your responsibility.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              Esta es la parte que más importa. Al registrar un sitio, nos dices que eres su dueño o que tienes
              autorización de quien lo es. La responsabilidad por esa autorización sigue siendo tuya: no la
              verificamos.
            </p>
            <p>Aceptas no apuntar Silent Puma a:</p>
            <ul className="nh-lista">
              <li>Sitios que no son tuyos y que no tienes permiso para analizar</li>
              <li>Áreas detrás de un inicio de sesión, o contenido que no fue hecho para ser público</li>
              <li>Ningún sitio de una forma que perjudique su funcionamiento normal</li>
            </ul>
            <p>
              Tú controlas el alcance y el límite de páginas al configurar cada ejecución. Ajustarlos con
              responsabilidad para el sitio de destino es decisión tuya, y responsabilidad tuya.
            </p>
          </>
        )
      }
    },
    {
      id: 'planos',
      titulo: { pt: 'Planos, limites e créditos', en: 'Plans, limits and credits', es: 'Planes, límites y créditos' },
      corpo: {
        pt: (
          <>
            <p>
              Cada plano tem limites de projetos, páginas por mês e créditos de IA. Os valores atuais estão na página de{' '}
              <Link to="/plans">Planos</Link>, que os lê da mesma configuração que o serviço aplica: o que você vê lá é o
              que vale.
            </p>
            <p>
              Chegar a um limite bloqueia a ação que o ultrapassaria; não interrompe o que já está rodando. Os créditos
              de IA renovam todo mês e não acumulam.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              Each plan carries limits on projects, pages per month and AI credits. The current values are on the{' '}
              <Link to="/plans">Plans</Link> page, which reads them from the same configuration the service enforces,
              so what you see there is what applies.
            </p>
            <p>
              Reaching a limit blocks the action that would exceed it; it does not interrupt work already running. AI
              credits reset monthly and do not carry over.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              Cada plan tiene límites de proyectos, páginas al mes y créditos de IA. Los valores actuales están en la
              página de <Link to="/plans">Planes</Link>, que los lee de la misma configuración que aplica el servicio:
              lo que ves ahí es lo que vale.
            </p>
            <p>
              Llegar a un límite bloquea la acción que lo superaría; no interrumpe lo que ya está en marcha. Los
              créditos de IA se renuevan cada mes y no se acumulan.
            </p>
          </>
        )
      }
    },
    {
      id: 'pagamento',
      titulo: { pt: 'Pagamento e cancelamento', en: 'Payments and cancellation', es: 'Pago y cancelación' },
      corpo: {
        pt: (
          <>
            <ul className="nh-lista">
              <li>Os planos pagos são cobrados por mês ou por ano, pelo Stripe, até serem cancelados</li>
              <li>
                O cancelamento vale no fim do período já pago: você mantém o acesso até lá, e depois a conta passa para
                o Basic
              </li>
              <li>Você mesmo cancela, quando quiser, nas configurações da conta</li>
              <li>O plano Basic é grátis e não pede cartão</li>
            </ul>
            <p>
              Não temos reembolso automático. Se você foi cobrado por algo que não usou ou não pretendia, escreva para
              nós e olhamos caso a caso.
            </p>
          </>
        ),
        en: (
          <>
            <ul className="nh-lista">
              <li>Paid plans are charged monthly or annually, through Stripe, until canceled</li>
              <li>
                Cancellation takes effect at the end of the period you already paid for: you keep access until then,
                and the account moves to Basic afterwards
              </li>
              <li>You can cancel yourself, at any time, in account settings</li>
              <li>The Basic plan is free and requires no card</li>
            </ul>
            <p>
              We do not run an automatic refund policy. If you were charged for something you did not use or did not
              intend, write to us and we will look at it case by case.
            </p>
          </>
        ),
        es: (
          <>
            <ul className="nh-lista">
              <li>Los planes de pago se cobran por mes o por año, a través de Stripe, hasta que se cancelan</li>
              <li>
                La cancelación vale al final del período ya pagado: conservas el acceso hasta entonces, y después la
                cuenta pasa a Basic
              </li>
              <li>Tú mismo cancelas, cuando quieras, en la configuración de la cuenta</li>
              <li>El plan Basic es gratis y no pide tarjeta</li>
            </ul>
            <p>
              No tenemos reembolso automático. Si te cobraron por algo que no usaste o no pretendías, escríbenos y lo
              revisamos caso por caso.
            </p>
          </>
        )
      }
    },
    {
      id: 'ia',
      titulo: { pt: 'Recomendações de IA', en: 'AI recommendations', es: 'Recomendaciones de IA' },
      corpo: {
        pt: (
          <>
            <p>
              A análise de IA é uma sugestão, não uma garantia. Ela é gerada a partir dos dados de uma única execução,
              por um modelo que pode errar ou deixar coisas de fora, e não conhece o seu negócio, o seu público nem as
              suas prioridades comerciais.
            </p>
            <p>
              Revise cada recomendação antes de aplicá-la a um site no ar. Não prometemos nenhuma posição, tráfego ou
              resultado de busca: ninguém pode prometer isso com honestidade.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              The AI analysis is a suggestion, not a guarantee. It is generated from the data collected in a single
              run, by a model that can be wrong or incomplete, and it does not know your business, your audience or
              your commercial priorities.
            </p>
            <p>
              Review each recommendation before applying it to a live site. We do not promise any particular ranking,
              traffic or search result: nobody honestly can.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              El análisis de IA es una sugerencia, no una garantía. Se genera a partir de los datos de una sola
              ejecución, con un modelo que puede equivocarse o dejar cosas fuera, y no conoce tu negocio, tu público ni
              tus prioridades comerciales.
            </p>
            <p>
              Revisa cada recomendación antes de aplicarla a un sitio publicado. No prometemos ninguna posición,
              tráfico ni resultado de búsqueda: nadie puede prometerlo con honestidad.
            </p>
          </>
        )
      }
    },
    {
      id: 'retencao',
      titulo: { pt: 'Retenção de dados', en: 'Data retention', es: 'Retención de datos' },
      corpo: {
        pt: (
          <p>
            Os dados de análise vencem conforme o seu plano e são apagados automaticamente. Exporte em CSV o que quiser
            guardar antes do vencimento. Os detalhes estão na{' '}
            <Link to="/privacy-policy#retencao">Política de Privacidade</Link>.
          </p>
        ),
        en: (
          <p>
            Analysis data expires according to your plan and is deleted automatically. Export what you need to keep,
            as CSV, before it expires. Details are in the <Link to="/privacy-policy#retencao">Privacy Policy</Link>.
          </p>
        ),
        es: (
          <p>
            Los datos de análisis vencen según tu plan y se borran automáticamente. Exporta en CSV lo que quieras
            guardar antes del vencimiento. Los detalles están en la{' '}
            <Link to="/privacy-policy#retencao">Política de Privacidad</Link>.
          </p>
        )
      }
    },
    {
      id: 'disponibilidade',
      titulo: { pt: 'Disponibilidade', en: 'Availability', es: 'Disponibilidad' },
      corpo: {
        pt: (
          <p>
            Trabalhamos para manter o serviço no ar, mas não oferecemos garantia de tempo no ar. Manutenção, falhas de
            fornecedores e rastreamentos interrompidos podem acontecer. Um rastreamento interrompido por uma
            reinicialização volta sozinho para a fila, mas recomeça do início em vez de continuar de onde parou.
          </p>
        ),
        en: (
          <p>
            We work to keep the service up, but we do not offer a guaranteed uptime commitment. Maintenance, provider
            failures and interrupted crawls can happen. A crawl interrupted by a restart returns to the queue on its
            own, though it starts over rather than resuming.
          </p>
        ),
        es: (
          <p>
            Trabajamos para mantener el servicio en línea, pero no ofrecemos garantía de disponibilidad. Pueden ocurrir
            mantenimientos, fallas de proveedores y rastreos interrumpidos. Un rastreo interrumpido por un reinicio
            vuelve solo a la cola, pero empieza de nuevo en vez de continuar donde se detuvo.
          </p>
        )
      }
    },
    {
      id: 'responsabilidade',
      titulo: {
        pt: 'Limite de responsabilidade',
        en: 'Limitation of liability',
        es: 'Limitación de responsabilidad'
      },
      corpo: {
        pt: (
          <>
            <p>
              Na medida em que a lei permite, o Silent Puma não responde por danos indiretos, lucros cessantes ou
              perdas que resultem de decisões tomadas com base nos dados ou nas recomendações que o serviço produz.
            </p>
            <p>
              Nada aqui limita a responsabilidade que a lei não permite limitar, incluindo os direitos do consumidor
              que se aplicam a você.
            </p>
          </>
        ),
        en: (
          <>
            <p>
              To the extent the law allows, Silent Puma is not liable for indirect damages, lost profits, or losses
              resulting from decisions you make based on the data or recommendations the service produces.
            </p>
            <p>
              Nothing here limits liability that cannot be limited by law, including consumer rights that apply to
              you.
            </p>
          </>
        ),
        es: (
          <>
            <p>
              En la medida en que la ley lo permite, Silent Puma no responde por daños indirectos, lucro cesante ni
              pérdidas que resulten de decisiones tomadas con base en los datos o las recomendaciones que produce el
              servicio.
            </p>
            <p>
              Nada aquí limita la responsabilidad que la ley no permite limitar, incluidos los derechos del consumidor
              que te correspondan.
            </p>
          </>
        )
      }
    },
    {
      id: 'fim',
      titulo: { pt: 'Fim do contrato', en: 'Ending the agreement', es: 'Fin del acuerdo' },
      corpo: {
        pt: (
          <p>
            Você pode apagar sua conta quando quiser, nas configurações da conta, e isso remove seus dados como
            descreve a <Link to="/privacy-policy#retencao">Política de Privacidade</Link>. Podemos suspender uma conta
            que descumpra <a href="#rastrear">o que você pode rastrear</a> ou que ponha o serviço em risco, e dizemos o
            motivo quando fizermos isso.
          </p>
        ),
        en: (
          <p>
            You may delete your account at any time from account settings, which removes your data as described in the{' '}
            <Link to="/privacy-policy#retencao">Privacy Policy</Link>. We may suspend an account that breaks{' '}
            <a href="#rastrear">What you may crawl</a> or puts the service at risk, and we will say why when we do.
          </p>
        ),
        es: (
          <p>
            Puedes borrar tu cuenta cuando quieras, en la configuración de la cuenta, y eso elimina tus datos como
            describe la <Link to="/privacy-policy#retencao">Política de Privacidad</Link>. Podemos suspender una cuenta
            que incumpla <a href="#rastrear">qué puedes rastrear</a> o que ponga el servicio en riesgo, y te diremos el
            motivo cuando lo hagamos.
          </p>
        )
      }
    },
    {
      id: 'mudancas',
      titulo: { pt: 'Mudanças nestes termos', en: 'Changes to these terms', es: 'Cambios en estos términos' },
      corpo: {
        pt: (
          <p>
            Quando estes termos mudam, atualizamos a data no topo. Se a mudança afetar o que você paga ou o que pode
            fazer com o serviço, avisamos por e-mail antes de ela valer.
          </p>
        ),
        en: (
          <p>
            When these terms change we update the date at the top. If a change affects what you pay or what you may do
            with the service, we will tell you by email before it takes effect.
          </p>
        ),
        es: (
          <p>
            Cuando estos términos cambian, actualizamos la fecha de arriba. Si el cambio afecta lo que pagas o lo que
            puedes hacer con el servicio, te avisamos por correo antes de que entre en vigor.
          </p>
        )
      }
    },
    {
      id: 'contato',
      titulo: { pt: 'Contato', en: 'Contact', es: 'Contacto' },
      corpo: {
        pt: (
          <p>
            Para dúvidas sobre estes termos, escreva para <Email />.
          </p>
        ),
        en: (
          <p>
            For questions about these terms, write to <Email />.
          </p>
        ),
        es: (
          <p>
            Para dudas sobre estos términos, escribe a <Email />.
          </p>
        )
      }
    }
  ]
};
